import React, { useState, useRef, useEffect } from "react";
import "../../styles/CarHotspotViewer.css";

const DEFAULT_HOTSPOTS = [
  {
    id: "front-bumper",
    label: "Front Bumper",
    x: 12,
    y: 72,
    condition: "good", 
    description: "No major dents or scratches. Minor surface scuffs.",
    icon: "🔍",
  },
  {
    id: "hood",
    label: "Hood / Bonnet",
    x: 28,
    y: 35,
    condition: "good",
    description: "Clean with no dents. Paint in excellent condition.",
    icon: "🔍",
  },
  {
    id: "windshield",
    label: "Windshield",
    x: 42,
    y: 22,
    condition: "good",
    description: "No cracks or chips. Wipers functional.",
    icon: "🔍",
  },
  {
    id: "roof",
    label: "Roof",
    x: 52,
    y: 12,
    condition: "good",
    description: "No dents. Minor surface oxidation.",
    icon: "🔍",
  },
  {
    id: "front-door",
    label: "Front Door",
    x: 38,
    y: 55,
    condition: "fair",
    description: "Small scratch on handle area. Repainted once.",
    icon: "🔍",
  },
  {
    id: "rear-door",
    label: "Rear Door",
    x: 58,
    y: 55,
    condition: "good",
    description: "Original paint. No visible damage.",
    icon: "🔍",
  },
  {
    id: "rear-bumper",
    label: "Rear Bumper",
    x: 86,
    y: 72,
    condition: "needs-attention",
    description: "Minor crack on lower lip. Functional but cosmetic repair suggested.",
    icon: "🔍",
  },
  {
    id: "front-wheel",
    label: "Front Tyre",
    x: 22,
    y: 86,
    condition: "good",
    description: "Tread depth: 6mm. Good condition.",
    icon: "🔍",
  },
  {
    id: "rear-wheel",
    label: "Rear Tyre",
    x: 72,
    y: 86,
    condition: "good",
    description: "Tread depth: 5mm. Good condition.",
    icon: "🔍",
  },
  {
    id: "headlight",
    label: "Headlight",
    x: 8,
    y: 48,
    condition: "good",
    description: "LED headlights working perfectly. No condensation.",
    icon: "🔍",
  },
  {
    id: "taillight",
    label: "Taillight",
    x: 90,
    y: 52,
    condition: "good",
    description: "All lights functional. No cracks.",
    icon: "🔍",
  },
];

const CONDITION_CONFIG = {
  good: {
    label: "Good",
    color: "#10b981",
    bg: "#d1fae5",
    border: "#10b981",
    dot: "#10b981",
    emoji: "✅",
  },
  fair: {
    label: "Fair",
    color: "#f59e0b",
    bg: "#fef3c7",
    border: "#f59e0b",
    dot: "#f59e0b",
    emoji: "⚠️",
  },
  "needs-attention": {
    label: "Needs Attention",
    color: "#ef4444",
    bg: "#fee2e2",
    border: "#ef4444",
    dot: "#ef4444",
    emoji: "🔧",
  },
};

const CarHotspotViewer = ({ car, hotspots: customHotspots }) => {
  const [activeHotspot, setActiveHotspot] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [tooltipSide, setTooltipSide] = useState("right");
  const containerRef = useRef(null);

  const hotspots = customHotspots || DEFAULT_HOTSPOTS;

  // Pick the best image to show hotspots on
  const hotspotImage =
    car?.hotspot_image_url
      ? `http://localhost:3001${car.hotspot_image_url}`
      : car?.exterior_images_urls?.[0]
      ? `http://localhost:3001${car.exterior_images_urls[0]}`
      : car?.images_urls?.[0]
      ? `http://localhost:3001${car.images_urls[0]}`
      : null;

  const conditionSummary = {
    good: hotspots.filter((h) => h.condition === "good").length,
    fair: hotspots.filter((h) => h.condition === "fair").length,
    "needs-attention": hotspots.filter((h) => h.condition === "needs-attention").length,
  };

  const handleHotspotClick = (e, hotspot) => {
    e.stopPropagation();
    if (activeHotspot?.id === hotspot.id) {
      setActiveHotspot(null);
      return;
    }
    // Decide tooltip direction
    const side = hotspot.x > 55 ? "left" : "right";
    setTooltipSide(side);
    setActiveHotspot(hotspot);
  };

  const closeTooltip = () => setActiveHotspot(null);

  if (!hotspotImage) {
    return (
      <div className="hotspot-viewer-empty">
        <div className="hotspot-empty-icon">🔍</div>
        <p>No inspection image available</p>
      </div>
    );
  }

  return (
    <div className="hotspot-viewer-wrapper">
      {/* Header */}
      <div className="hotspot-viewer-header">
        <div className="hotspot-header-left">
          <span className="hotspot-badge-icon">🔍</span>
          <div>
            <h3 className="hotspot-title">360° Inspection Hotspots</h3>
            <p className="hotspot-subtitle">Tap any point to see inspection details</p>
          </div>
        </div>
        <div className="hotspot-legend">
          <span className="legend-item good">
            <span className="legend-dot" style={{ background: "#10b981" }} />
            Good ({conditionSummary.good})
          </span>
          <span className="legend-item fair">
            <span className="legend-dot" style={{ background: "#f59e0b" }} />
            Fair ({conditionSummary.fair})
          </span>
          <span className="legend-item needs-attention">
            <span className="legend-dot" style={{ background: "#ef4444" }} />
            Needs Attention ({conditionSummary["needs-attention"]})
          </span>
        </div>
      </div>

      {/* Image + Hotspot Overlay */}
      <div
        className="hotspot-image-container"
        ref={containerRef}
        onClick={closeTooltip}
      >
        {/* Loading skeleton */}
        {!imageLoaded && (
          <div className="hotspot-image-skeleton">
            <div className="skeleton-shimmer" />
          </div>
        )}

        <img
          src={hotspotImage}
          alt="Car inspection view"
          className={`hotspot-base-image ${imageLoaded ? "loaded" : ""}`}
          onLoad={() => setImageLoaded(true)}
          draggable={false}
        />

        {/* Render hotspot dots */}
        {imageLoaded &&
          hotspots.map((hotspot) => {
            const cfg = CONDITION_CONFIG[hotspot.condition];
            const isActive = activeHotspot?.id === hotspot.id;

            return (
              <div
                key={hotspot.id}
                className={`hotspot-point ${isActive ? "active" : ""}`}
                style={{ left: `${hotspot.x}%`, top: `${hotspot.y}%` }}
                onClick={(e) => handleHotspotClick(e, hotspot)}
                title={hotspot.label}
              >
                {/* Pulse ring */}
                <span
                  className="hotspot-pulse-ring"
                  style={{ borderColor: cfg.dot }}
                />
                {/* Dot */}
                <span
                  className="hotspot-dot-inner"
                  style={{ background: cfg.dot }}
                />

                {/* Tooltip */}
                {isActive && (
                  <div
                    className={`hotspot-tooltip ${tooltipSide}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button className="tooltip-close" onClick={closeTooltip}>
                      ×
                    </button>
                    <div className="tooltip-header">
                      <span className="tooltip-label">{hotspot.label}</span>
                      <span
                        className="tooltip-condition-badge"
                        style={{
                          background: cfg.bg,
                          color: cfg.color,
                          border: `1px solid ${cfg.border}`,
                        }}
                      >
                        {cfg.emoji} {cfg.label}
                      </span>
                    </div>
                    <p className="tooltip-description">{hotspot.description}</p>
                    <div
                      className="tooltip-footer"
                      style={{ background: cfg.bg, borderColor: cfg.border }}
                    >
                      <span style={{ color: cfg.color, fontWeight: 700 }}>
                        {cfg.emoji} {cfg.label}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

        {/* Watermark badge */}
        <div className="hotspot-watermark">
          <span>🔍</span> Inspected by AucwiniCars
        </div>
      </div>

      {/* Bottom summary strip */}
      <div className="hotspot-summary-strip">
        <div className="summary-stat">
          <span className="summary-number" style={{ color: "#10b981" }}>
            {conditionSummary.good}
          </span>
          <span className="summary-label">Good</span>
        </div>
        <div className="summary-divider" />
        <div className="summary-stat">
          <span className="summary-number" style={{ color: "#f59e0b" }}>
            {conditionSummary.fair}
          </span>
          <span className="summary-label">Fair</span>
        </div>
        <div className="summary-divider" />
        <div className="summary-stat">
          <span
            className="summary-number"
            style={{ color: "#ef4444" }}
          >
            {conditionSummary["needs-attention"]}
          </span>
          <span className="summary-label">Needs Attention</span>
        </div>
        <div className="summary-divider" />
        <div className="summary-stat">
          <span className="summary-number" style={{ color: "#003566" }}>
            {hotspots.length}
          </span>
          <span className="summary-label">Total Points</span>
        </div>
      </div>
    </div>
  );
};

export default CarHotspotViewer;